import React from "react";

/**
 * Blox proprietary icon system — React-rendered inline SVG.
 * 40 icons across 8 categories sharing a single design DNA:
 * isometric perspective, layered geometric depth, diamond/hexagonal motifs.
 *
 * Ported from blox.global/src/components/shared.tsx. Single source of truth
 * is brand/BRANDING.md §3.3 — the diamond is the BLOX hexagonal-logo signature.
 *
 * Two call styles supported:
 *   <GeoIcon shape="vault" color={BloxColor.green} size={28} />   ← preferred
 *   <GeoIcon name="vault-green" />                                ← legacy, kept for back-compat
 *
 * The legacy `name` form parses a trailing `-{green|cyan|violet|amber}` color
 * suffix and falls through to the `shape` API. Existing call sites that pass
 * `name="wire-cyan"` keep working unchanged.
 */

export const BloxColor = {
  green: "#22C55E",   // Treasury — primary brand
  cyan: "#06B6D4",    // Payments
  violet: "#8B5CF6",  // AI Agent Pay
  amber: "#F59E0B",   // Alerts / accents
};

const COLOR_SUFFIXES = {
  green: BloxColor.green,
  cyan: BloxColor.cyan,
  violet: BloxColor.violet,
  amber: BloxColor.amber,
};

function parseLegacyName(name) {
  if (!name) return { shape: undefined, color: undefined };
  const idx = name.lastIndexOf("-");
  if (idx > 0) {
    const suffix = name.slice(idx + 1);
    if (COLOR_SUFFIXES[suffix]) {
      return { shape: name.slice(0, idx), color: COLOR_SUFFIXES[suffix] };
    }
  }
  return { shape: name, color: undefined };
}

function GeoIcon({
  shape: shapeProp,
  name,
  color: colorProp,
  size = 28,
  className = "icon",
  style,
}) {
  const legacy = parseLegacyName(name);
  const shape = shapeProp || legacy.shape;
  const color = colorProp || legacy.color || BloxColor.green;
  const s = size;

  const diamond = (cx, cy, r) =>
    `M${cx} ${cy - r} L${cx + r * 1.2} ${cy} L${cx} ${cy + r} L${cx - r * 1.2} ${cy} Z`;

  const wrap = (children) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
      aria-hidden="true"
    >
      {children}
    </svg>
  );

  switch (shape) {
    // ───────── ORIGINAL 12 ─────────
    case "bolt":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d={diamond(24, 24, 12)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.06" opacity="0.5" />
          <path d="M26 12 L18 26 L23 26 L21 36 L30 22 L25 22 Z" fill={color} fillOpacity="0.85" />
        </>
      );
    case "vault":
      return wrap(
        <>
          <path d="M24 6 L42 16 L42 32 L24 42 L6 32 L6 16 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d="M24 12 L36 19 L36 29 L24 36 L12 29 L12 19 Z" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.06" />
          <path d={diamond(24, 24, 5)} fill={color} fillOpacity="0.3" />
          <path d={diamond(24, 24, 2.5)} fill={color} fillOpacity="0.7" />
          <line x1="24" y1="19" x2="24" y2="29" stroke={color} strokeWidth="0.8" opacity="0.3" />
          <line x1="19" y1="24" x2="29" y2="24" stroke={color} strokeWidth="0.8" opacity="0.3" />
        </>
      );
    case "globe":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <ellipse cx="24" cy="24" rx="8" ry="14" stroke={color} strokeWidth="0.8" opacity="0.4" transform="rotate(-30 24 24)" />
          <ellipse cx="24" cy="24" rx="8" ry="14" stroke={color} strokeWidth="0.8" opacity="0.4" transform="rotate(30 24 24)" />
          <path d={diamond(24, 24, 12)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.25" />
          <path d={diamond(24, 24, 6)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.2" />
          <path d={diamond(24, 24, 2)} fill={color} fillOpacity="0.6" />
        </>
      );
    case "plug":
      return wrap(
        <>
          <path d={diamond(24, 24, 16)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <rect x="19" y="8" width="3.5" height="10" rx="1" fill={color} fillOpacity="0.6" transform="rotate(-15 20.75 13)" />
          <rect x="26" y="8" width="3.5" height="10" rx="1" fill={color} fillOpacity="0.6" transform="rotate(15 27.75 13)" />
          <path d="M15 22 L24 17 L33 22 L33 28 L24 33 L15 28 Z" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.08" />
          <path d={diamond(24, 25, 3)} fill={color} fillOpacity="0.5" />
          <line x1="24" y1="33" x2="24" y2="42" stroke={color} strokeWidth="1.2" opacity="0.4" />
          <circle cx="24" cy="42" r="2" fill={color} fillOpacity="0.3" />
        </>
      );
    case "target":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.8" fill="none" opacity="0.2" />
          <path d={diamond(24, 24, 14)} stroke={color} strokeWidth="1" fill={color} fillOpacity="0.03" opacity="0.4" />
          <path d={diamond(24, 24, 10)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <path d={diamond(24, 24, 6)} stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" />
          <path d={diamond(24, 24, 2.5)} fill={color} fillOpacity="0.9" />
        </>
      );
    case "layers":
      return wrap(
        <>
          <path d="M24 10 L40 19 L24 28 L8 19 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.12" />
          <path d="M24 18 L40 27 L24 36 L8 27 Z" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.08" opacity="0.7" />
          <path d="M24 26 L40 35 L24 44 L8 35 Z" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.05" opacity="0.45" />
          <path d={diamond(24, 19, 3)} fill={color} fillOpacity="0.4" />
        </>
      );
    case "shield":
      return wrap(
        <>
          <path d="M24 4 L40 12 L40 26 C40 34 24 44 24 44 C24 44 8 34 8 26 L8 12 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d="M24 10 L34 16 L34 25 C34 30 24 37 24 37 C24 37 14 30 14 25 L14 16 Z" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.06" opacity="0.5" />
          <path d={diamond(24, 23, 5)} fill={color} fillOpacity="0.15" />
          <path d="M19 24 L22.5 27.5 L30 19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </>
      );
    case "chart":
      return wrap(
        <>
          <path d={diamond(24, 40, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <path d="M10 38 L14 36 L14 30 L10 32 Z" fill={color} fillOpacity="0.3" />
          <path d="M14 36 L18 34 L18 28 L14 30 Z" fill={color} fillOpacity="0.35" />
          <path d="M18 34 L22 32 L22 22 L18 24 Z" fill={color} fillOpacity="0.5" />
          <path d="M22 32 L26 30 L26 20 L22 22 Z" fill={color} fillOpacity="0.55" />
          <path d="M26 30 L30 28 L30 16 L26 18 Z" fill={color} fillOpacity="0.7" />
          <path d="M30 28 L34 26 L34 12 L30 14 Z" fill={color} fillOpacity="0.8" />
          <path d="M34 26 L38 24 L38 8 L34 10 Z" fill={color} fillOpacity="0.9" />
          <path d={diamond(37, 8, 2)} fill={color} />
        </>
      );
    case "agent":
      return wrap(
        <>
          <path d="M24 6 L40 16 L40 32 L24 42 L8 32 L8 16 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d={diamond(24, 11, 5)} stroke={color} strokeWidth="0.8" fill="none" opacity="0.3" />
          <path d={diamond(24, 11, 2.5)} fill={color} fillOpacity="0.6" />
          <line x1="24" y1="14" x2="24" y2="20" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <path d={diamond(24, 27, 7)} stroke={color} strokeWidth="1" fill={color} fillOpacity="0.12" />
          <path d={diamond(24, 27, 3.5)} fill={color} fillOpacity="0.85" />
          <line x1="14" y1="36" x2="34" y2="36" stroke={color} strokeWidth="0.8" opacity="0.35" />
        </>
      );
    case "stream":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <line x1="4" y1="24" x2="8" y2="24" stroke={color} strokeWidth="0.8" opacity="0.3" />
          <path d={diamond(11, 24, 2.5)} fill={color} fillOpacity="0.2" />
          <path d={diamond(19, 24, 3)} fill={color} fillOpacity="0.4" />
          <path d={diamond(28, 24, 3.5)} fill={color} fillOpacity="0.65" />
          <path d={diamond(38, 24, 4)} fill={color} fillOpacity="0.9" />
          <line x1="44" y1="24" x2="46" y2="24" stroke={color} strokeWidth="0.8" opacity="0.4" />
        </>
      );
    case "brain":
      return wrap(
        <>
          <path d="M24 6 L40 16 L40 32 L24 42 L8 32 L8 16 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <line x1="24" y1="10" x2="24" y2="38" stroke={color} strokeWidth="0.6" opacity="0.25" />
          <path d={diamond(15, 17, 2.5)} fill={color} fillOpacity="0.35" />
          <path d={diamond(15, 24, 2.5)} fill={color} fillOpacity="0.5" />
          <path d={diamond(15, 31, 2.5)} fill={color} fillOpacity="0.35" />
          <path d={diamond(33, 17, 2.5)} fill={color} fillOpacity="0.35" />
          <path d={diamond(33, 24, 2.5)} fill={color} fillOpacity="0.5" />
          <path d={diamond(33, 31, 2.5)} fill={color} fillOpacity="0.35" />
          <path d={diamond(24, 24, 3)} fill={color} fillOpacity="0.95" />
          <line x1="18" y1="24" x2="20.5" y2="24" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="27.5" y1="24" x2="30" y2="24" stroke={color} strokeWidth="0.6" opacity="0.4" />
        </>
      );
    case "network":
      return wrap(
        <>
          <path d={diamond(24, 24, 5)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.15" />
          <path d={diamond(10, 12, 3.5)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.1" />
          <path d={diamond(38, 12, 3.5)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.1" />
          <path d={diamond(10, 36, 3.5)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.1" />
          <path d={diamond(38, 36, 3.5)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.1" />
          <line x1="20" y1="21" x2="13" y2="15" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <line x1="28" y1="21" x2="35" y2="15" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <line x1="20" y1="27" x2="13" y2="33" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <line x1="28" y1="27" x2="35" y2="33" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <path d={diamond(24, 24, 2)} fill={color} fillOpacity="0.8" />
        </>
      );

    // ───────── BANKING & MONEY ─────────
    case "coin":
      return wrap(
        <>
          <ellipse cx="24" cy="28" rx="14" ry="4" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.04" />
          <ellipse cx="24" cy="22" rx="14" ry="4" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.08" />
          <line x1="10" y1="22" x2="10" y2="28" stroke={color} strokeWidth="1" opacity="0.4" />
          <line x1="38" y1="22" x2="38" y2="28" stroke={color} strokeWidth="1" opacity="0.4" />
          <path d={diamond(24, 22, 5)} stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.15" />
          <path d={diamond(24, 22, 2)} fill={color} fillOpacity="0.9" />
        </>
      );
    case "wallet":
      return wrap(
        <>
          <path d="M8 14 L40 14 L40 36 L8 36 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d="M8 14 L24 8 L40 14" stroke={color} strokeWidth="1" fill="none" opacity="0.5" />
          <path d="M28 22 L40 22 L40 30 L28 30 Z" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.08" />
          <path d={diamond(34, 26, 2.5)} fill={color} fillOpacity="0.85" />
        </>
      );
    case "card":
      return wrap(
        <>
          <rect x="6" y="14" width="36" height="22" rx="3" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d={diamond(15, 22, 3)} stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.4" />
          <path d={diamond(15, 22, 1.4)} fill={color} fillOpacity="0.85" />
          <line x1="22" y1="20" x2="38" y2="20" stroke={color} strokeWidth="0.6" opacity="0.3" />
          <line x1="22" y1="24" x2="34" y2="24" stroke={color} strokeWidth="0.6" opacity="0.3" />
          <line x1="11" y1="30" x2="38" y2="30" stroke={color} strokeWidth="0.8" opacity="0.4" />
        </>
      );
    case "bank":
      return wrap(
        <>
          <path d={diamond(24, 11, 4)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.15" />
          <path d="M8 18 L24 12 L40 18 L40 21 L8 21 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <line x1="12" y1="22" x2="12" y2="34" stroke={color} strokeWidth="1.5" opacity="0.7" />
          <line x1="19" y1="22" x2="19" y2="34" stroke={color} strokeWidth="1.5" opacity="0.7" />
          <line x1="29" y1="22" x2="29" y2="34" stroke={color} strokeWidth="1.5" opacity="0.7" />
          <line x1="36" y1="22" x2="36" y2="34" stroke={color} strokeWidth="1.5" opacity="0.7" />
          <path d="M6 35 L42 35 L42 38 L6 38 Z" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.08" />
        </>
      );
    case "ledger":
      return wrap(
        <>
          <path d="M8 12 L24 16 L24 40 L8 36 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <path d="M40 12 L24 16 L24 40 L40 36 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <line x1="13" y1="20" x2="20" y2="22" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="13" y1="25" x2="20" y2="27" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="13" y1="30" x2="20" y2="32" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="28" y1="22" x2="35" y2="20" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="28" y1="27" x2="35" y2="25" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <path d={diamond(32, 14, 2.5)} fill={color} fillOpacity="0.85" />
        </>
      );

    // ───────── BLOCKCHAIN INFRASTRUCTURE ─────────
    case "block":
      return wrap(
        <>
          <path d="M24 8 L40 17 L40 33 L24 42 L8 33 L8 17 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <path d="M24 8 L40 17 L24 26 L8 17 Z" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.12" />
          <path d="M24 26 L24 42" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <path d="M8 17 L8 33" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <path d="M40 17 L40 33" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <path d={diamond(24, 17, 3.5)} fill={color} fillOpacity="0.85" />
        </>
      );
    case "chain":
      return wrap(
        <>
          <path d={diamond(11, 24, 6)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <path d={diamond(11, 24, 3)} fill="none" stroke={color} strokeWidth="0.8" opacity="0.5" />
          <path d={diamond(24, 24, 6)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.12" />
          <path d={diamond(24, 24, 3)} fill={color} fillOpacity="0.8" />
          <path d={diamond(37, 24, 6)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <path d={diamond(37, 24, 3)} fill="none" stroke={color} strokeWidth="0.8" opacity="0.5" />
        </>
      );
    case "bridge":
      return wrap(
        <>
          <path d={diamond(10, 30, 5)} stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" />
          <path d={diamond(10, 30, 2)} fill={color} fillOpacity="0.6" />
          <path d={diamond(38, 30, 5)} stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" />
          <path d={diamond(38, 30, 2)} fill={color} fillOpacity="0.6" />
          <path d="M10 25 Q24 6 38 25" stroke={color} strokeWidth="1.5" fill="none" />
          <path d="M14 27 Q24 14 34 27" stroke={color} strokeWidth="0.6" fill="none" opacity="0.4" />
          <path d={diamond(24, 14, 2.5)} fill={color} fillOpacity="0.9" />
          <line x1="6" y1="36" x2="42" y2="36" stroke={color} strokeWidth="0.8" opacity="0.3" />
        </>
      );
    case "gas":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.12" />
          <path d="M24 8 C18 16 14 20 14 28 C14 34 18 40 24 40 C30 40 34 34 34 28 C34 20 30 16 24 8 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <path d="M24 16 C21 22 19 24 19 30 C19 33 21 36 24 36 C27 36 29 33 29 30 C29 24 27 22 24 16 Z" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.1" opacity="0.6" />
          <path d={diamond(24, 30, 3.5)} fill={color} fillOpacity="0.9" />
        </>
      );

    // ───────── CRYPTOGRAPHY ─────────
    case "key":
      return wrap(
        <>
          <path d={diamond(15, 24, 8)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.06" />
          <path d={diamond(15, 24, 4)} stroke={color} strokeWidth="0.8" fill="none" opacity="0.4" />
          <path d={diamond(15, 24, 1.6)} fill={color} fillOpacity="0.85" />
          <line x1="23" y1="24" x2="40" y2="24" stroke={color} strokeWidth="2" />
          <line x1="34" y1="24" x2="34" y2="30" stroke={color} strokeWidth="2" />
          <line x1="38" y1="24" x2="38" y2="28" stroke={color} strokeWidth="2" />
        </>
      );
    case "signature":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <path d="M8 30 Q14 20 18 26 Q22 32 26 22 Q30 14 34 24" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <line x1="8" y1="36" x2="40" y2="36" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <path d={diamond(36, 24, 4)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.15" />
          <path d={diamond(36, 24, 1.8)} fill={color} fillOpacity="0.9" />
        </>
      );
    case "hash":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <line x1="18" y1="10" x2="14" y2="38" stroke={color} strokeWidth="2" opacity="0.85" />
          <line x1="30" y1="10" x2="26" y2="38" stroke={color} strokeWidth="2" opacity="0.85" />
          <line x1="10" y1="18" x2="38" y2="18" stroke={color} strokeWidth="2" opacity="0.85" />
          <line x1="9" y1="30" x2="37" y2="30" stroke={color} strokeWidth="2" opacity="0.85" />
          <path d={diamond(24, 24, 2)} fill={color} fillOpacity="0.9" />
        </>
      );

    // ───────── ASSET TYPES ─────────
    case "stablecoin":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.04" />
          <path d={diamond(24, 24, 13)} stroke={color} strokeWidth="1.2" fill="none" opacity="0.5" />
          <path d={diamond(24, 24, 9)} stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.08" />
          <path d="M24 16 L24 32" stroke={color} strokeWidth="1.5" />
          <path d="M28 19 C28 18 26 17 24 17 C22 17 20 18 20 20 C20 22 22 23 24 24 C26 25 28 26 28 28 C28 30 26 31 24 31 C22 31 20 30 20 29" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </>
      );
    case "nft":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.4" fill="none" opacity="0.3" strokeDasharray="2 2" />
          <path d={diamond(24, 24, 11)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.08" />
          <line x1="20" y1="18" x2="18" y2="30" stroke={color} strokeWidth="1.5" />
          <line x1="28" y1="18" x2="26" y2="30" stroke={color} strokeWidth="1.5" />
          <line x1="16" y1="22" x2="32" y2="22" stroke={color} strokeWidth="1.5" />
          <line x1="14" y1="27" x2="30" y2="27" stroke={color} strokeWidth="1.5" />
        </>
      );

    // ───────── DEFI ─────────
    case "pool":
      return wrap(
        <>
          <ellipse cx="24" cy="14" rx="16" ry="4" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <ellipse cx="24" cy="20" rx="13" ry="3" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.06" opacity="0.7" />
          <ellipse cx="24" cy="26" rx="10" ry="2.5" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.08" opacity="0.5" />
          <ellipse cx="24" cy="32" rx="6" ry="1.8" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.1" opacity="0.4" />
          <path d="M8 14 L18 38" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <path d="M40 14 L30 38" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <path d={diamond(24, 38, 2)} fill={color} fillOpacity="0.9" />
        </>
      );
    case "swap":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <path d="M14 16 L34 16" stroke={color} strokeWidth="2" />
          <path d="M30 12 L34 16 L30 20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M34 32 L14 32" stroke={color} strokeWidth="2" />
          <path d="M18 28 L14 32 L18 36" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <path d={diamond(24, 24, 3)} fill={color} fillOpacity="0.9" />
        </>
      );
    case "yield":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <circle cx="16" cy="18" r="3" stroke={color} strokeWidth="1.5" fill="none" />
          <circle cx="30" cy="32" r="3" stroke={color} strokeWidth="1.5" fill="none" />
          <line x1="32" y1="14" x2="14" y2="36" stroke={color} strokeWidth="2" />
          <path d="M8 36 Q16 28 24 24 Q32 20 38 12" stroke={color} strokeWidth="1.2" fill="none" opacity="0.5" />
          <path d={diamond(38, 12, 2.5)} fill={color} fillOpacity="0.9" />
        </>
      );
    case "stake":
      return wrap(
        <>
          <path d={diamond(24, 24, 12)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.08" />
          <path d={diamond(24, 24, 6)} fill={color} fillOpacity="0.85" />
          <ellipse cx="24" cy="24" rx="16" ry="6" stroke={color} strokeWidth="0.8" fill="none" opacity="0.4" transform="rotate(35 24 24)" />
          <ellipse cx="24" cy="24" rx="16" ry="6" stroke={color} strokeWidth="0.8" fill="none" opacity="0.4" transform="rotate(-35 24 24)" />
          <rect x="22" y="8" width="4" height="3" rx="0.5" fill={color} fillOpacity="0.7" />
          <rect x="22" y="37" width="4" height="3" rx="0.5" fill={color} fillOpacity="0.7" />
        </>
      );
    case "amm":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <line x1="8" y1="40" x2="40" y2="40" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <line x1="8" y1="8" x2="8" y2="40" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <path d="M8 36 Q16 36 20 28 Q24 16 40 12" stroke={color} strokeWidth="2" fill="none" />
          <path d="M8 38 Q14 38 18 32 Q22 22 38 18" stroke={color} strokeWidth="0.6" fill="none" opacity="0.35" />
          <path d={diamond(24, 22, 2.5)} fill={color} fillOpacity="0.9" />
        </>
      );

    // ───────── TRADING ─────────
    case "orderbook":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <rect x="14" y="8" width="14" height="2" rx="0.5" fill={color} fillOpacity="0.3" />
          <rect x="12" y="12" width="18" height="2" rx="0.5" fill={color} fillOpacity="0.4" />
          <rect x="10" y="16" width="22" height="2" rx="0.5" fill={color} fillOpacity="0.55" />
          <line x1="6" y1="22" x2="42" y2="22" stroke={color} strokeWidth="0.8" opacity="0.5" />
          <path d={diamond(24, 24, 2)} fill={color} fillOpacity="0.95" />
          <line x1="6" y1="26" x2="42" y2="26" stroke={color} strokeWidth="0.8" opacity="0.5" />
          <rect x="16" y="30" width="20" height="2" rx="0.5" fill={color} fillOpacity="0.55" />
          <rect x="18" y="34" width="16" height="2" rx="0.5" fill={color} fillOpacity="0.4" />
          <rect x="20" y="38" width="12" height="2" rx="0.5" fill={color} fillOpacity="0.3" />
        </>
      );
    case "candle":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <line x1="14" y1="10" x2="14" y2="38" stroke={color} strokeWidth="0.8" opacity="0.5" />
          <rect x="11" y="16" width="6" height="14" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.3" />
          <line x1="32" y1="6" x2="32" y2="42" stroke={color} strokeWidth="1" opacity="0.7" />
          <rect x="28" y="12" width="8" height="20" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.7" />
          <path d={diamond(32, 22, 2)} fill={color} fillOpacity="0.95" />
        </>
      );
    case "depth":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <path d="M8 36 L8 30 L14 30 L14 24 L20 24 L20 18 L24 18" stroke={color} strokeWidth="1.5" fill="none" />
          <path d="M8 36 L8 30 L14 30 L14 24 L20 24 L20 18 L24 18 L24 36 Z" fill={color} fillOpacity="0.18" />
          <path d="M40 36 L40 28 L34 28 L34 22 L28 22 L28 18 L24 18" stroke={color} strokeWidth="1.5" fill="none" />
          <path d="M40 36 L40 28 L34 28 L34 22 L28 22 L28 18 L24 18 L24 36 Z" fill={color} fillOpacity="0.1" />
          <line x1="24" y1="14" x2="24" y2="38" stroke={color} strokeWidth="0.6" opacity="0.4" strokeDasharray="2 2" />
          <path d={diamond(24, 18, 2)} fill={color} fillOpacity="0.95" />
        </>
      );

    // ───────── PAYMENT FLOWS ─────────
    case "wire":
      return wrap(
        <>
          <path d="M6 18 L42 18" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <path d="M6 30 L42 30" stroke={color} strokeWidth="0.8" opacity="0.4" />
          <path d={diamond(11, 24, 4)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.15" />
          <path d={diamond(11, 24, 1.5)} fill={color} fillOpacity="0.6" />
          <path d="M16 24 L34 24" stroke={color} strokeWidth="2" />
          <path d="M30 20 L34 24 L30 28" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <path d={diamond(38, 24, 3)} fill={color} fillOpacity="0.9" />
        </>
      );
    case "batch":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          {[
            [12, 12], [20, 12], [28, 12], [36, 12],
            [12, 20], [20, 20], [28, 20], [36, 20],
            [12, 28], [20, 28], [28, 28], [36, 28],
            [12, 36], [20, 36], [28, 36], [36, 36],
          ].map(([x, y], i) => (
            <path
              key={i}
              d={diamond(x, y, 2)}
              fill={color}
              fillOpacity={0.3 + ((x + y) % 16) / 22}
            />
          ))}
        </>
      );
    case "exchange":
      return wrap(
        <>
          <path d={diamond(24, 24, 18)} stroke={color} strokeWidth="0.6" fill="none" opacity="0.15" />
          <path d="M8 18 L40 18" stroke={color} strokeWidth="2" />
          <path d="M36 14 L40 18 L36 22" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M40 30 L8 30" stroke={color} strokeWidth="2" />
          <path d="M12 26 L8 30 L12 34" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <path d={diamond(24, 24, 2.5)} fill={color} fillOpacity="0.95" />
        </>
      );

    // ───────── OPERATIONS ─────────
    case "clock":
      return wrap(
        <>
          <circle cx="24" cy="24" r="17" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d={diamond(24, 9, 1.5)} fill={color} fillOpacity="0.5" />
          <path d={diamond(39, 24, 1.5)} fill={color} fillOpacity="0.5" />
          <path d={diamond(24, 39, 1.5)} fill={color} fillOpacity="0.5" />
          <path d={diamond(9, 24, 1.5)} fill={color} fillOpacity="0.5" />
          <line x1="24" y1="24" x2="24" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" />
          <line x1="24" y1="24" x2="32" y2="28" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
          <path d={diamond(24, 24, 2)} fill={color} fillOpacity="0.95" />
        </>
      );
    case "audit":
      return wrap(
        <>
          <rect x="10" y="10" width="28" height="34" rx="2" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <rect x="18" y="6" width="12" height="6" rx="1" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" />
          <line x1="14" y1="22" x2="34" y2="22" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="14" y1="28" x2="34" y2="28" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="14" y1="34" x2="28" y2="34" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <path d={diamond(34, 36, 5)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.15" />
          <path d="M31 36 L33.5 38.5 L37 35" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case "contract":
      return wrap(
        <>
          <path d="M10 6 L32 6 L38 12 L38 42 L10 42 Z" stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.04" />
          <path d="M32 6 L32 12 L38 12" stroke={color} strokeWidth="0.8" fill="none" opacity="0.5" />
          <line x1="14" y1="20" x2="34" y2="20" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="14" y1="25" x2="34" y2="25" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <line x1="14" y1="30" x2="28" y2="30" stroke={color} strokeWidth="0.6" opacity="0.4" />
          <path d={diamond(24, 38, 4)} stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.2" />
          <path d={diamond(24, 38, 2)} fill={color} fillOpacity="0.95" />
        </>
      );

    default:
      // Unknown shape — render a diamond placeholder so the call site still aligns
      return wrap(
        <>
          <path d={diamond(24, 24, 14)} stroke={color} strokeWidth="1.2" fill={color} fillOpacity="0.08" />
          <path d={diamond(24, 24, 5)} fill={color} fillOpacity="0.6" />
        </>
      );
  }
}

export default GeoIcon;
